import type { GPConfig } from "../types"
import {
  DEFAULT_POP_SIZE,
  DEFAULT_GENERATIONS,
  DEFAULT_ELITE,
  DEFAULT_CX_PROB,
  DEFAULT_CROSSOVER_CHILDREN,
  DEFAULT_IMMIGRATION,
  DEFAULT_TOURNAMENT,
  DEFAULT_STALL,
  DEFAULT_TARGET_FITNESS,
  DEFAULT_MAX_WALL_MS,
  DEFAULT_MAX_EVALUATIONS,
  DEFAULT_CONCURRENCY,
} from "./defaults"

/**
 * Normalize GPConfig to shared defaults without instantiating the engine.
 *
 * Guidance:
 * - Breeding proportions: Pc + Pm = 1 (immigration is separate).
 * - If mutProb is omitted, we derive it as 1 - cxProb.
 * - timeLimitMs is a deprecated alias of maxWallMs (kept for back-compat).
 */
export function normalizeConfigBase<T>(
  cfg: GPConfig<T>,
): Required<
  Pick<
    GPConfig<T>,
    | "popSize"
    | "generations"
    | "elite"
    | "cxProb"
    | "mutProb"
    | "crossoverChildren"
    | "immigration"
    | "tournament"
    | "stall"
    | "targetFitness"
    | "timeLimitMs"
    | "maxWallMs"
    | "maxEvaluations"
    | "concurrency"
  >
> {
  const popSize = cfg.popSize ?? DEFAULT_POP_SIZE
  const elite = cfg.elite ?? DEFAULT_ELITE(popSize)
  const maxWallMs = cfg.maxWallMs ?? cfg.timeLimitMs ?? DEFAULT_MAX_WALL_MS
  const cxProb = cfg.cxProb ?? DEFAULT_CX_PROB
  // Ensure breeding operator simplex when caller omits mutProb
  const mutProb = cfg.mutProb ?? 1 - cxProb
  return {
    popSize,
    generations: cfg.generations ?? DEFAULT_GENERATIONS,
    elite,
    cxProb,
    mutProb,
    crossoverChildren: cfg.crossoverChildren ?? DEFAULT_CROSSOVER_CHILDREN,
    immigration: cfg.immigration ?? DEFAULT_IMMIGRATION,
    tournament: cfg.tournament ?? DEFAULT_TOURNAMENT,
    stall: cfg.stall ?? DEFAULT_STALL,
    targetFitness: cfg.targetFitness ?? DEFAULT_TARGET_FITNESS,
    timeLimitMs: maxWallMs,
    maxWallMs,
    maxEvaluations: cfg.maxEvaluations ?? DEFAULT_MAX_EVALUATIONS,
    concurrency: cfg.concurrency ?? DEFAULT_CONCURRENCY,
  }
}
